/**
 * usePWAInstall — hook React pour gérer l'installation PWA.
 *
 * Sur Chrome/Edge/Android : le navigateur émet `beforeinstallprompt` — on
 * intercepte l'événement pour l'utiliser plus tard sur un bouton custom.
 *
 * Sur iOS Safari : pas de prompt natif — on détecte le device et on retourne
 * `platform: "ios"` pour afficher des instructions manuelles ("Partager →
 * Ajouter à l'écran d'accueil").
 *
 * Retourne :
 *   - canInstall : boolean → afficher/masquer le CTA d'install
 *   - isInstalled : boolean → déjà installé (display-mode standalone)
 *   - platform : "chromium" | "ios" | "other" → adapter le message
 *   - install() : Promise<"accepted" | "dismissed" | "unsupported">
 */

import { useEffect, useState, useCallback } from "react";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports "Macintosh" but has touch — detect explicitly.
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  if (isIOS) return "ios";
  if (/Chrome|CriOS|EdgA|Edg/.test(ua)) return "chromium";
  return "other";
};

export function usePWAInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [platform] = useState(detectPlatform());

  useEffect(() => {
    const onPrompt = (e) => {
      // Empêche Chrome de montrer son propre mini-bandeau — on gère nous-mêmes.
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (installed) return "unsupported";
    if (!deferred) return "unsupported";
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice?.outcome || "dismissed";
    } catch {
      return "dismissed";
    }
  }, [deferred, installed]);

  // canInstall = on peut afficher le CTA :
  //  - Chromium : un event prompt a été capturé
  //  - iOS      : jamais de prompt natif mais on montre l'UI d'instructions
  const canInstall = !installed && (Boolean(deferred) || platform === "ios");

  return { canInstall, isInstalled: installed, platform, install };
}
